// Simplified EmailNotificationsTab.jsx - Remove optimistic updates
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import { useAppConfig } from '../../hooks/useAppConfig';
import { Switch } from '@headlessui/react';
import {
    EnvelopeIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function EmailNotificationsTab() {
    const { data: appConfig } = useAppConfig();
    const queryClient = useQueryClient();
    const [testingEmail, setTestingEmail] = useState(false);

    const { data: emailSettings, isLoading, refetch } = useQuery(
        'emailNotifications',
        settingsAPI.getEmailNotifications,
        {
            retry: 1,
            refetchOnWindowFocus: false,
            onError: (error) => {
                console.error('Email settings error:', error);
                toast.error('Failed to load email settings');
            }
        }
    );

    // Simple mutation without optimistic updates
    const updateMutation = useMutation(settingsAPI.saveEmailNotifications, {
        onSuccess: async (response) => {
            // console.log('✅ Mutation success, response:', response);

            // Force a fresh fetch
            await refetch();

            toast.success('Email notification settings saved!');
        },
        onError: (error) => {
            console.error('❌ Mutation error:', error);
            toast.error(error.response?.data?.message || 'Failed to save email settings');
        },
    });

    const testEmailMutation = useMutation(settingsAPI.testApprovalEmail, {
        onSuccess: () => {
            toast.success('Test email sent! Check your inbox.');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to send test email');
        },
    });

    const handleSettingChange = (field, value) => {
        // console.log(`🔄 Changing ${field} to:`, value);

        // FIX: Access the correct nested data structure
        const currentSettings = emailSettings?.data?.data || emailSettings?.data || {};
        // console.log('📊 Current settings extracted:', currentSettings);

        const updatedSettings = {
            ...currentSettings,
            [field]: value
        };

        // console.log('📤 Sending settings:', updatedSettings);
        updateMutation.mutate(updatedSettings);
    };

    const handleEmailChange = (email) => {
        clearTimeout(window.emailUpdateTimeout);
        window.emailUpdateTimeout = setTimeout(() => {
            handleSettingChange('approvalEmailAddress', email);
        }, 1000);
    };

    const handleTestEmail = () => {
        setTestingEmail(true);
        testEmailMutation.mutate();
        setTimeout(() => setTestingEmail(false), 2000);
    };

    if (isLoading) {
        return <div className="animate-pulse">Loading email notification settings...</div>;
    }

    const settings = emailSettings?.data?.data || emailSettings?.data || {};
    const isEmailConfigured = appConfig?.features?.emailFullyConfigured ?? false;

    // Explicit boolean conversion with logging
    const emailNotificationsEnabled = Boolean(settings.emailNotifications ?? true);
    const approvalEmailEnabled = Boolean(settings.approvalEmailEnabled ?? true);

    // console.log('🎯 Toggle Debug Info:', {
    //     rawEmailSettings: emailSettings,
    //     extractedSettings: settings,
    //     emailNotifications: settings.emailNotifications,
    //     emailNotificationsEnabled,
    //     approvalEmailEnabled,
    //     dataType: typeof settings.emailNotifications,
    //     updateMutationLoading: updateMutation.isLoading
    // });

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900">Email Notifications</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Configure when and where to receive email notifications for your posts
                </p>
            </div>

            {/* Debug Panel */}
            {/*<div className="bg-gray-100 p-3 rounded text-xs">*/}
            {/*    <strong>Debug:</strong> emailNotifications = {JSON.stringify(settings.emailNotifications)} |*/}
            {/*    enabled = {JSON.stringify(emailNotificationsEnabled)} |*/}
            {/*    loading = {JSON.stringify(updateMutation.isLoading)}*/}
            {/*</div>*/}

            {/* Email Service Status */}
            <div className={`rounded-lg border p-4 ${isEmailConfigured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                <div className="flex items-center">
                    <div className="flex-shrink-0">
                        {isEmailConfigured ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-400" />
                        ) : (
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                        )}
                    </div>
                    <div className="ml-3">
                        <h4 className={`text-sm font-medium ${isEmailConfigured ? 'text-green-800' : 'text-yellow-800'}`}>
                            {isEmailConfigured ? 'Email Service Active' : 'Email Service Not Configured'}
                        </h4>
                        <p className={`text-sm ${isEmailConfigured ? 'text-green-700' : 'text-yellow-700'}`}>
                            {isEmailConfigured
                                ? 'SendGrid is configured and ready to send notifications'
                                : 'Email notifications require SendGrid configuration by the administrator'
                            }
                        </p>
                    </div>
                </div>
            </div>

            {/* General Email Notifications */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-6 w-6 text-gray-400" />
                        <div>
                            <h4 className="text-base font-medium text-gray-900">Email Notifications</h4>
                            <p className="text-sm text-gray-600">Receive email notifications for account activities</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-3">
                        {updateMutation.isLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        )}

                        <Switch
                            checked={emailNotificationsEnabled}
                            onChange={(enabled) => {
                                // console.log('🔘 Switch clicked, changing from', emailNotificationsEnabled, 'to', enabled);
                                handleSettingChange('emailNotifications', enabled);
                            }}
                            disabled={updateMutation.isLoading || !isEmailConfigured}
                            className={`${
                                emailNotificationsEnabled ? 'bg-blue-600' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            <span
                                className={`${
                                    emailNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                            />
                        </Switch>
                    </div>
                </div>
            </div>

            {/* Approval Email Settings */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-base font-medium text-gray-900 mb-4">Post Approval Notifications</h4>

                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h5 className="text-sm font-medium text-gray-900">Approval Email Alerts</h5>
                        <p className="text-sm text-gray-600">Get notified when new posts are ready for review</p>
                    </div>

                    <div className="flex items-center space-x-3">
                        {updateMutation.isLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        )}

                        <Switch
                            checked={approvalEmailEnabled}
                            onChange={(enabled) => {
                                // console.log('🔘 Approval switch clicked, changing from', approvalEmailEnabled, 'to', enabled);
                                handleSettingChange('approvalEmailEnabled', enabled);
                            }}
                            disabled={updateMutation.isLoading || !emailNotificationsEnabled || !isEmailConfigured}
                            className={`${
                                approvalEmailEnabled ? 'bg-blue-600' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            <span
                                className={`${
                                    approvalEmailEnabled ? 'translate-x-5' : 'translate-x-0'
                                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                            />
                        </Switch>
                    </div>
                </div>

                {/* Email Address Configuration */}
                {approvalEmailEnabled && emailNotificationsEnabled && (
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="approvalEmail" className="block text-sm font-medium text-gray-700">
                                Approval Email Address
                            </label>
                            <div className="mt-1">
                                <input
                                    type="email"
                                    id="approvalEmail"
                                    defaultValue={settings.approvalEmailAddress || settings.email}
                                    onChange={(e) => handleEmailChange(e.target.value)}
                                    placeholder={settings.email}
                                    disabled={updateMutation.isLoading || !isEmailConfigured}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                Leave blank to use your account email ({settings.email})
                            </p>
                        </div>

                        {/* Test Email Button */}
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={handleTestEmail}
                                disabled={testEmailMutation.isLoading || testingEmail || !isEmailConfigured}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <EnvelopeIcon className="h-4 w-4 mr-2" />
                                {testingEmail ? 'Sending...' : 'Send Test Email'}
                            </button>

                            {(testEmailMutation.isLoading || testingEmail) && (
                                <div className="flex items-center text-sm text-gray-500">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                    Sending test email...
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Manual Refresh Button for Testing */}
            {/*<div className="bg-gray-50 p-4 rounded">*/}
            {/*    <button*/}
            {/*        onClick={() => {*/}
            {/*            console.log('🔄 Manual refresh triggered');*/}
            {/*            refetch();*/}
            {/*        }}*/}
            {/*        className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"*/}
            {/*    >*/}
            {/*        🔄 Refresh Settings (Debug)*/}
            {/*    </button>*/}
            {/*</div>*/}

            {/* Information Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">About Email Notifications</h3>
                        <div className="mt-2 text-sm text-blue-700">
                            <ul className="list-disc list-inside space-y-1">
                                <li><strong>Approval Emails:</strong> Sent when AI generates a new post that needs your review</li>
                                <li><strong>Default Address:</strong> Uses your account email unless you specify a different one</li>
                                <li><strong>Privacy:</strong> Your email preferences are private and not shared</li>
                                <li><strong>Instant Delivery:</strong> Emails are sent immediately when posts are generated</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EmailNotificationsTab;